/**
 * Completion Step Component
 * TASK-339: Onboarding Wizard UI
 *
 * Success page shown after onboarding is complete
 */

import React from 'react';
import { CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import KielisLogo from '../../../../assets/KielisLogo-no-bg.png';

interface CompletionStepProps {
  companyName: string;
  onLogin: () => void;
}

const CompletionStep: React.FC<CompletionStepProps> = ({
  companyName,
  onLogin,
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-kielis-glacier to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200">
          {/* Logo */}
          <img
            src={KielisLogo}
            alt="Kielis"
            className="h-12 mx-auto mb-6"
          />

          {/* Success icon */}
          <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center relative">
            <CheckCircle size={40} className="text-green-600" />
            <div className="absolute -top-1 -right-1">
              <Sparkles size={24} className="text-kielis-gold" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome to Kielis!
          </h1>
          <p className="text-gray-600 mb-6">
            Your account for <strong>{companyName}</strong> is ready to use.
          </p>

          {/* What's next */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm font-medium text-gray-700 mb-3">What's next?</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <span className="w-5 h-5 rounded-full bg-kielis-navy text-white text-xs flex items-center justify-center flex-shrink-0">1</span>
                <span>Explore your dashboard and get familiar with the interface</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <span className="w-5 h-5 rounded-full bg-kielis-navy text-white text-xs flex items-center justify-center flex-shrink-0">2</span>
                <span>Add your first client through the Clients page</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600">
                <span className="w-5 h-5 rounded-full bg-kielis-navy text-white text-xs flex items-center justify-center flex-shrink-0">3</span>
                <span>Start a meeting to experience AI-powered advisory support</span>
              </li>
            </ul>
          </div>

          {/* Login button */}
          <button
            onClick={onLogin}
            className="w-full py-3 px-4 bg-kielis-navy text-white font-medium rounded-lg hover:bg-opacity-90 transition-colors flex items-center justify-center gap-2"
          >
            Go to Dashboard
            <ArrowRight size={18} />
          </button>

          {/* Support link */}
          <p className="mt-6 text-xs text-gray-500">
            Need help?{' '}
            <a href="mailto:support@kielis.ch" className="text-kielis-navy hover:underline">
              Contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompletionStep;
